import type { GraphNode } from "@mind/contracts/browser";
import { Search, X } from "lucide-react";
import { useNavigate, type NavigateFn } from "@tanstack/react-router";
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { graphApi } from "../../features/graph/graphApi.js";
import { destinationForGraphNode, type CommandDestination } from "./routeState.js";
import styles from "./CommandSearchDialog.module.css";

type SearchState = "idle" | "loading" | "ready" | "error";

type CommandSearchKey = "focus" | "work" | "graph" | "documents";

const COMMANDS: readonly { readonly key: CommandSearchKey; readonly labelKey: `nav.${CommandSearchKey}` }[] = [
  { key: "focus", labelKey: "nav.focus" },
  { key: "work", labelKey: "nav.work" },
  { key: "graph", labelKey: "nav.graph" },
  { key: "documents", labelKey: "nav.documents" },
];

export type CommandSearchDialogProps = {
  readonly open: boolean;
  readonly workspaceId: string;
  readonly dialogRef: RefObject<HTMLDialogElement | null>;
  readonly onRequestOpen: () => void;
  readonly onRequestClose: () => void;
};

type NavigateFunction = NavigateFn;

async function navigateDestination(navigate: NavigateFunction, destination: CommandDestination): Promise<void> {
  switch (destination.to) {
    case "/documents":
      await navigate({ to: "/documents", search: destination.search });
      return;
    case "/focus":
      await navigate({ to: "/focus", search: destination.search });
      return;
    case "/graph":
      await navigate({ to: "/graph", search: destination.search });
      return;
    case "/work":
      await navigate({ to: "/work", search: destination.search });
  }
}

function typeLabelKey(node: GraphNode): string {
  return `commandSearch.types.${node.type}`;
}

export function CommandSearchDialog({
  open,
  workspaceId,
  dialogRef,
  onRequestOpen,
  onRequestClose,
}: CommandSearchDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const titleId = useId();
  const inputId = useId();
  const [query, setQuery] = useState("");
  const [state, setState] = useState<SearchState>("idle");
  const [results, setResults] = useState<readonly GraphNode[]>([]);
  const [retryGeneration, setRetryGeneration] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onRequestOpen();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onRequestOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;
    if (open) {
      if (!dialog.open) dialog.showModal();
      queueMicrotask(() => inputRef.current?.focus());
      return;
    }
    if (dialog.open) dialog.close();
  }, [dialogRef, open]);

  useEffect(() => {
    if (!open) return;
    const normalizedQuery = query.trim();
    if (normalizedQuery.length === 0) {
      setState("idle");
      setResults([]);
      return;
    }
    if (workspaceId.length === 0) return;
    const controller = new AbortController();
    setState("loading");
    void graphApi.searchRoots(workspaceId, normalizedQuery, null, controller.signal)
      .then((result) => {
        setResults(result.items);
        setState("ready");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setState("error");
      });
    return () => controller.abort();
  }, [open, query, retryGeneration, workspaceId]);

  const close = useCallback(() => {
    dialogRef.current?.close();
    onRequestClose();
  }, [dialogRef, onRequestClose]);

  const navigateTo = useCallback(async (destination: CommandDestination) => {
    close();
    await navigateDestination(navigate, destination);
  }, [close, navigate]);

  const commandDestination = useCallback((key: CommandSearchKey): CommandDestination => {
    switch (key) {
      case "focus":
        return { to: "/focus", search: { workspace: workspaceId, mode: "new" } };
      case "work":
        return { to: "/work", search: { workspace: workspaceId } };
      case "graph":
        return { to: "/graph", search: { workspace: workspaceId } };
      case "documents":
        return { to: "/documents", search: { workspace: workspaceId } };
    }
  }, [workspaceId]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    inputRef.current?.focus();
  };

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby={titleId}
      onClose={onRequestClose}
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        close();
      }}
    >
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{t("commandSearch.eyebrow")}</p>
          <h2 id={titleId}>{t("commandSearch.title")}</h2>
        </div>
        <button type="button" className={styles.closeButton} onClick={close} aria-label={t("common.close")}>
          <X aria-hidden="true" size={20} />
        </button>
      </div>
      <form className={styles.form} onSubmit={submit}>
        <Search aria-hidden="true" size={20} />
        <input
          ref={inputRef}
          id={inputId}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("commandSearch.placeholder")}
          aria-label={t("commandSearch.inputLabel")}
          autoComplete="off"
          spellCheck={false}
        />
      </form>
      <div className={styles.content} aria-live="polite">
        {query.trim().length === 0 ? (
          <section aria-labelledby={`${titleId}-commands`}>
            <h3 id={`${titleId}-commands`}>{t("commandSearch.destinations")}</h3>
            <ul className={styles.list}>
              {COMMANDS.map((command) => (
                <li key={command.key}>
                  <button type="button" className={styles.resultButton} onClick={() => void navigateTo(commandDestination(command.key))}>
                    <span className={styles.resultCopy}>
                      <strong>{t(command.labelKey)}</strong>
                      <small>{t(`commandSearch.commandDescriptions.${command.key}`)}</small>
                    </span>
                    <span aria-hidden="true">→</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : state === "loading" ? (
          <p className={styles.status} role="status">{t("commandSearch.loading")}</p>
        ) : state === "error" ? (
          <div className={styles.error} role="alert">
            <p>{t("commandSearch.error")}</p>
            <button type="button" className={styles.secondaryButton} onClick={() => setRetryGeneration((current) => current + 1)}>{t("common.retry")}</button>
          </div>
        ) : results.length === 0 ? (
          <p className={styles.status}>{t("commandSearch.noResults", { query: query.trim() })}</p>
        ) : (
          <ul className={styles.list}>
            {results.map((node) => (
              <li key={`${node.type}:${node.id}`}>
                <button
                  type="button"
                  className={styles.resultButton}
                  onClick={() => void navigateTo(destinationForGraphNode(node, workspaceId))}
                >
                  <span className={styles.resultCopy}>
                    <strong>{node.label}</strong>
                    <small>
                      {t(typeLabelKey(node))}
                      {node.tags.length > 0 ? ` · ${node.tags.join(", ")}` : ""}
                      {node.archived ? ` · ${t("commandSearch.archived")}` : ""}
                    </small>
                  </span>
                  <span aria-hidden="true">→</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </dialog>
  );
}
